import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import RecentActorsOutlinedIcon from '@mui/icons-material/RecentActorsOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useMediaQuery } from '@components/Common/DesktopOnly';
import { useCurrentUserRole } from '@components/Common/Utils/roles';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import {
  COMMANDS,
  GO_TO,
  SEARCHABLE_ENTITIES,
  pushRecent,
  readRecents,
} from './paletteActions';
import {
  ENTITY_META,
  recogniseId,
  searchAll,
  type PaletteEntity,
  type PaletteRecord,
} from './paletteSearch';
import './index.scss';

const DEBOUNCE_MS = 250;
const MIN_QUERY = 2;

const ENTITY_ICON: Record<PaletteEntity, typeof SearchIcon> = {
  job: BusinessCenterOutlinedIcon,
  company: ApartmentOutlinedIcon,
  employer: RecentActorsOutlinedIcon,
  jobseeker: PersonOutlineOutlinedIcon,
  application: DescriptionOutlinedIcon,
};

/** A flat, keyboard-navigable row. Groups are a rendering concern only. */
interface Row {
  key: string;
  group: string;
  title: string;
  subtitle: string;
  status?: string;
  hint: string;
  path: string;
  Icon: typeof SearchIcon;
  tone: string;
  tint: string;
  /** Records get remembered; commands and go-to rows do not. */
  record?: PaletteRecord;
}

const toRow = (record: PaletteRecord): Row => {
  const meta = ENTITY_META[record.entity];
  return {
    key: `${record.entity}:${record.id}`,
    group: meta.group,
    title: record.title,
    subtitle: record.subtitle,
    status: record.status,
    hint: meta.label,
    path: record.path,
    Icon: ENTITY_ICON[record.entity],
    tone: meta.tone,
    tint: meta.tint,
    record,
  };
};

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const role = useCurrentUserRole();
  // The palette is full-screen on a phone, so its close button is the only
  // way out and has to be a thumb-sized target.
  const isPhone = !useMediaQuery('(min-width: 768px)');

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<Record<PaletteEntity, PaletteRecord[]> | null>(null);
  const [typeFilter, setTypeFilter] = useState<PaletteEntity | null>(null);
  const [selected, setSelected] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  /** Guards against a slow response overwriting a newer one. */
  const requestId = useRef(0);

  // Reset on every open — the palette should never show the last search.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setRecords(null);
    setTypeFilter(null);
    setSelected(0);
    setLoading(false);
    const focus = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(focus);
  }, [open]);

  // Debounced server-side search.
  useEffect(() => {
    if (!open) return;
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY || recogniseId(trimmed).length > 0) {
      setRecords(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const id = ++requestId.current;
    const timer = setTimeout(async () => {
      const result = await searchAll(trimmed, SEARCHABLE_ENTITIES);
      if (id !== requestId.current) return; // a newer query already landed
      setRecords(result);
      setLoading(false);
      setSelected(0);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, open]);

  /** Everything visible right now, flattened in render order. */
  const rows = useMemo<Row[]>(() => {
    const trimmed = query.trim();

    // A pasted record id jumps straight there — no search round-trip.
    const byId = recogniseId(trimmed);
    if (byId.length) return byId.map(toRow);

    // Empty state: the last five records this user opened.
    if (trimmed.length < MIN_QUERY) {
      const recents = readRecents().map(toRow);
      return recents.map((row) => ({ ...row, group: 'Recent' }));
    }

    const out: Row[] = [];
    const needle = trimmed.toLowerCase();

    for (const entity of SEARCHABLE_ENTITIES) {
      if (typeFilter && typeFilter !== entity) continue;
      for (const record of records?.[entity] ?? []) out.push(toRow(record));
    }

    if (!typeFilter) {
      const terms = needle.split(/\s+/).filter(Boolean);
      for (const command of COMMANDS) {
        if (!command.visible({ role })) continue;
        const relevant =
          terms.some((t) => command.match.some((m) => m.startsWith(t) || t.startsWith(m))) ||
          command.title.toLowerCase().includes(needle);
        if (!relevant) continue;
        out.push({
          key: command.id,
          group: 'Commands',
          title: command.title,
          subtitle: command.subtitle,
          hint: command.hint,
          path: command.path,
          Icon: BoltOutlinedIcon,
          tone: 'var(--green)',
          tint: 'var(--green-tint)',
        });
      }

      for (const target of GO_TO) {
        if (!target.visible({ role })) continue;
        if (!target.title.toLowerCase().includes(needle) && !target.subtitle.toLowerCase().includes(needle)) continue;
        out.push({
          key: target.id,
          group: 'Go to',
          title: target.title,
          subtitle: target.subtitle,
          hint: target.hint,
          path: target.path,
          Icon: ArrowForwardOutlinedIcon,
          tone: 'var(--tx-mid)',
          tint: 'var(--ink-700)',
        });
      }
    }

    return out;
  }, [query, records, typeFilter, role]);

  /** Consecutive rows sharing a group render under one label. */
  const grouped = useMemo(() => {
    const out: { group: string; rows: { row: Row; index: number }[] }[] = [];
    rows.forEach((row, index) => {
      const last = out[out.length - 1];
      if (last && last.group === row.group) last.rows.push({ row, index });
      else out.push({ group: row.group, rows: [{ row, index }] });
    });
    return out;
  }, [rows]);

  const activate = useCallback(
    (row: Row) => {
      if (row.record) pushRecent(row.record);
      onClose();
      navigate(row.path);
    },
    [navigate, onClose],
  );

  // Keep the selected row in view as the user arrows through.
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-palette-index="${selected}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  // Escape also closes from outside the input. The input handler below covers
  // the normal case, but focus lands there a frame after the palette paints,
  // and a click on a result group or the scrollbar moves it again — pressing
  // Escape in either window otherwise did nothing. The palette is aria-modal,
  // so while it is open nothing else has a claim on the key.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Escape is handled by the document listener above, not here.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((i) => (rows.length ? (i + 1) % rows.length : 0));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((i) => (rows.length ? (i - 1 + rows.length) % rows.length : 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[selected];
      if (row) activate(row);
      return;
    }
    if (e.key === 'Tab') {
      // Cycle the type filter: all → jobs → companies → … → all.
      e.preventDefault();
      const order: (PaletteEntity | null)[] = [null, ...SEARCHABLE_ENTITIES];
      const next = order[(order.indexOf(typeFilter) + 1) % order.length];
      setTypeFilter(next);
      setSelected(0);
    }
  };

  if (!open) return null;

  const isEmptyState = query.trim().length < MIN_QUERY;

  return (
    <div className="cc-palette-backdrop" onMouseDown={onClose} role="presentation">
      <div
        className="cc-palette"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        {/* ── Input row ── */}
        <div className="cc-palette__input-row">
          <SearchIcon className="cc-palette__search-icon" />
          <input
            ref={inputRef}
            className="cc-palette__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search jobs, companies, seekers, employers, applications…"
            aria-label="Search or run a command"
          />
          {typeFilter && (
            <span className="cc-palette__type-filter">{ENTITY_META[typeFilter].label}</span>
          )}
          {loading && <CircularProgress size={14} className="cc-palette__spinner" />}
          {/*
            The esc hint is a keyboard affordance, and on a phone the palette
            is full-screen: no backdrop to tap, no Esc key, and picking a
            result is a navigation, not a way out. Until this button existed
            there was no way to close the search on a phone at all.
          */}
          <span className="cc-palette__esc">esc</span>
          <IconButton
            aria-label="Close search"
            className="cc-palette__close"
            size={isPhone ? 'large' : 'medium'}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </div>

        {/* ── Results ── */}
        <div className="cc-palette__results" ref={listRef}>
          {rows.length === 0 && (
            <Box className="cc-palette__empty">
              <Typography className="cc-palette__empty-text">
                {isEmptyState
                  ? 'Search for a record, or type a command.'
                  : loading
                    ? 'Searching…'
                    : 'Nothing matched. Results are scoped to your role.'}
              </Typography>
            </Box>
          )}

          {grouped.map((group) => (
            <div key={group.group}>
              <div className="cc-palette__group-label">{group.group}</div>
              {group.rows.map(({ row, index }) => (
                <div
                  key={row.key}
                  data-palette-index={index}
                  className={`cc-palette__row${index === selected ? ' cc-palette__row--selected' : ''}`}
                  onMouseEnter={() => setSelected(index)}
                  onClick={() => activate(row)}
                  role="button"
                  tabIndex={-1}
                >
                  <span className="cc-palette__row-tile" style={{ backgroundColor: row.tint }}>
                    <row.Icon sx={{ fontSize: 16, color: row.tone }} />
                  </span>
                  <span className="cc-palette__row-text">
                    <span className="cc-palette__row-title">{row.title}</span>
                    {row.subtitle && <span className="cc-palette__row-sub">{row.subtitle}</span>}
                  </span>
                  {row.status && (
                    <span className="cc-palette__row-chip">
                      <CRMStatusChip status={row.status} />
                    </span>
                  )}
                  <span className="cc-palette__row-hint">{row.hint}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* ── Footer ── */}
        <div className="cc-palette__footer">
          <span className="cc-palette__legend"><kbd>↑↓</kbd>navigate</span>
          <span className="cc-palette__legend"><kbd>↵</kbd>open</span>
          <span className="cc-palette__legend"><kbd>tab</kbd>filter by type</span>
          <span className="cc-palette__footer-spacer" />
          <span className="cc-palette__scope">Results are scoped to your role</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
